import { Routes } from '@angular/router';
import { authGuard, workspaceSessionGuard } from '@core/guards/auth.guard';
import { LayoutComponent } from './features/layout/layout.component';
import { DashboardPage } from './features/dashboard/dashboard-page';
import { FormsListPage } from './features/forms/pages/forms-list-page/forms-list-page';
import { AiFormBuilderPage } from './features/forms/pages/ai-form-builder-page/ai-form-builder-page';
import { FormPreviewPage } from './features/forms/pages/form-preview-page/form-preview-page';
import { FormPublishedPage } from './features/forms/pages/form-published-page/form-published-page';
import { FormDataPage } from './features/forms/pages/form-data-page/form-data-page';
import { ShortLinkRedirectPage } from './features/forms/pages/short-link-redirect-page/short-link-redirect-page';
import { PublicFormPage } from './features/forms/pages/public-form-page/public-form-page';
import { publicFormRouteCanMatch } from '@core/guards/public-form-route.can-match';
import { HomePage } from '@public/home-page/home-page';
import { PricingPage } from '@public/pricing-page/pricing-page';
import { PublicLayoutComponent } from '@public/public-layout/public-layout';
import { UseCasesPage } from '@public/use-cases-page/use-cases-page';
import { ContactSalesPage } from '@public/contact-sales-page/contact-sales-page';
import { MyFormsAccessPage } from '@my-forms/pages/access-page/my-forms-access-page';
import { MyFormsInboxPage } from '@my-forms/pages/inbox-page/my-forms-inbox-page';
import { MyFormsLayoutComponent } from '@my-forms/layout/my-forms-layout';
import { MyFormsGoogleSuccessPage } from '@my-forms/auth/google-success-page/my-forms-google-success-page';
import { MyFormsGoogleErrorPage } from '@my-forms/auth/google-error-page/my-forms-google-error-page';
import { respondentAuthGuard } from '@core/guards/respondent-auth.guard';
import { SignupPage } from './features/auth/signup-page/signup-page';
import { LoginPage } from './features/auth/login-page/login-page';
import { OnboardingPage } from './features/auth/onboarding-page/onboarding-page';
import { GoogleSuccessPage } from './features/auth/google-success-page/google-success-page';
import { GoogleErrorPage } from './features/auth/google-error-page/google-error-page';
import { NoAccountPage } from './features/auth/no-account-page/no-account-page';
import { AcceptInvitePage } from './features/auth/accept-invite-page/accept-invite-page';
import { CreateWorkspacePage } from './features/auth/create-workspace-page/create-workspace-page';
import { CreateCompanyPage } from './features/auth/create-company-page/create-company-page';
import { SelectWorkspacePage } from './features/auth/select-workspace-page/select-workspace-page';
import { TeamMembersPage } from './features/team-members/team-members-page/team-members-page';
import { IntegrationsPage } from './features/integrations/integrations-page';
import { SettingsPage } from './features/settings/settings-page';
import { SystemVariablesDocsPage } from './features/docs/system-variables-docs-page';
import { DataLibraryPage } from './features/data-library/data-library-page';
import { CreateCollectionPage } from './features/data-library/create-collection-page/create-collection-page';
import { CollectionDetailPage } from './features/data-library/collection-detail-page/collection-detail-page';
import { PeoplePage } from './features/people/people-page';
import { ContactsPage } from './features/people/contacts-page/contacts-page';
import { GroupsPage } from './features/people/groups-page/groups-page';
import { ToastTestPage } from './features/toast-test/toast-test-page';
import { Test } from './features/test/test';

export const routes: Routes = [
  { path: 'home', redirectTo: '', pathMatch: 'full' },
  { path: 'signup', component: SignupPage },
  { path: 'login', component: LoginPage },
  { path: 'auth/google/success', component: GoogleSuccessPage },
  { path: 'auth/google/error', component: GoogleErrorPage },
  { path: 'auth/my-forms/google/success', component: MyFormsGoogleSuccessPage },
  { path: 'auth/my-forms/google/error', component: MyFormsGoogleErrorPage },
  { path: 'auth/no-account', component: NoAccountPage },
  { path: 'auth/google/callback', redirectTo: 'auth/google/error', pathMatch: 'full' },
  { path: 'accept-invite', component: AcceptInvitePage },
  { path: 'create-company', component: CreateCompanyPage },
  { path: 'create-workspace', component: CreateWorkspacePage },
  { path: 'select-workspace', component: SelectWorkspacePage },
  { path: 'onboarding', component: OnboardingPage },
  { path: 'f/:code', component: ShortLinkRedirectPage },
  {
    path: 'forms/preview/:slug',
    component: FormPreviewPage,
    canActivate: [authGuard, workspaceSessionGuard],
  },
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: '', component: HomePage, pathMatch: 'full' },
      { path: 'pricing', component: PricingPage },
      { path: 'use-cases', component: UseCasesPage },
      { path: 'contact-sales', component: ContactSalesPage },
    ],
  },
  {
    path: '',
    component: MyFormsLayoutComponent,
    children: [
      { path: 'my-forms', component: MyFormsAccessPage },
      {
        path: 'my-forms/inbox',
        component: MyFormsInboxPage,
        canActivate: [respondentAuthGuard],
      },
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard, workspaceSessionGuard],
    children: [
      { path: 'dashboard', component: DashboardPage },
      { path: 'forms/published/:slug', component: FormPublishedPage },
      {
        path: 'forms/builder/:slug',
        component: AiFormBuilderPage,
        data: { hideTopBar: true },
      },
      { path: 'forms/:slug/data', component: FormDataPage },
      { path: 'forms', component: FormsListPage },
      { path: 'data-library/collections/new', component: CreateCollectionPage },
      { path: 'data-library/collections/:id', component: CollectionDetailPage },
      { path: 'data-library', component: DataLibraryPage },
      {
        path: 'people',
        component: PeoplePage,
        children: [
          { path: '', redirectTo: 'contacts', pathMatch: 'full' },
          { path: 'contacts', component: ContactsPage },
          { path: 'groups', component: GroupsPage },
        ],
      },
      { path: 'team-members', component: TeamMembersPage },
      { path: 'integrations', component: IntegrationsPage },
      { path: 'settings', component: SettingsPage },
      { path: 'docs', redirectTo: 'docs/system-variables', pathMatch: 'full' },
      {
        path: 'docs/system-variables',
        component: SystemVariablesDocsPage,
      },
      { path: 'toastTest', component: ToastTestPage },
    ],
  },
  {
    path: ':workspaceSlug/:formSlug',
    component: PublicFormPage,
    canMatch: [publicFormRouteCanMatch],
  },

  {path:'test', component:Test}
];

